// Coalescing for socket-driven React updates (#1813).
//
// React 19 throws error #185 ("Maximum update depth exceeded") from *any*
// `setState` once 50 consecutive commits have each landed while another update
// was already queued. `commitRootImpl` keeps that counter:
//
//   0 !== (committedLanes & 4194218) && 0 !== (root.pendingLanes & 42)
//     ? root === rootWithNestedUpdates ? nestedUpdateCount++ : (nestedUpdateCount = 0, …)
//     : (nestedUpdateCount = 0);
//
// It resets on the first commit that lands with an empty queue — so the counter
// only accumulates while updates keep arriving *during* React's own render and
// commit window. A live websocket feed does exactly that: after the display has
// slept (or the window has been backgrounded), the browser delivers the buffered
// backlog as one dense burst, each message calling `setState` straight from
// `onmessage`. Fifty-one such commits in a row and the *next* update from
// anywhere throws — which is why the reported stack in #1813 is an innocent
// `AppStatus` handler rather than whatever was actually looping.
//
// React 18 did not have this failure mode: its equivalent check was
// `remainingLanes === SyncLane`, ignoring the default lane that ordinary async
// socket updates use. React 19 broadened the mask to `42`
// (Sync | InputContinuous | Default), so normal live-data churn now counts.
//
// The fix is to stop dispatching one React update per message. A coalescer
// buffers the burst and flushes once from a plain macrotask, which has two
// effects: N messages become one commit, and that commit is scheduled from an
// idle task rather than from inside React's render window, so it lands with an
// empty queue and *resets* the counter instead of advancing it.

export interface Coalescer {
  // Request a flush. Repeated calls before the flush runs collapse into one.
  schedule(): void;
  // Drop a pending flush (unmount).
  cancel(): void;
  // Run a pending flush immediately, if there is one.
  flush(): void;
}

export function createCoalescer(run: () => void): Coalescer {
  let handle: ReturnType<typeof setTimeout> | undefined;
  const fire = () => {
    handle = undefined;
    run();
  };
  return {
    schedule() {
      if (handle !== undefined) {
        return;
      }
      handle = setTimeout(fire, 0);
    },
    cancel() {
      if (handle === undefined) {
        return;
      }
      clearTimeout(handle);
      handle = undefined;
    },
    flush() {
      if (handle === undefined) {
        return;
      }
      clearTimeout(handle);
      fire();
    },
  };
}

// Structural equality for a socket payload, used to drop a broadcast that says
// nothing new. `AppStatus` is re-broadcast on a timer as well as on change, so a
// moored boat republishes an identical status indefinitely; storing it created a
// brand-new object each time and re-rendered the whole app for nothing.
//
// Payloads are small, flat-ish generated DTOs, so a serialize-and-compare is both
// cheap and exact. Key order is stable because both sides are produced by the same
// encoder, and a serialization failure (a cycle, a BigInt) just reports "changed",
// which is the safe answer.
export function samePayload(a: unknown, b: unknown): boolean {
  if (a === b) {
    return true;
  }
  if (a === undefined || b === undefined || a === null || b === null) {
    return false;
  }
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}
